'use client';

import { AnimatePresence, motion } from 'motion/react';
import Form from 'next/form';
import { useActionState } from 'react';
import { useFormStatus } from 'react-dom';

import {
  registerNewsletterContact,
  type RegisterNewsletterContactMessageState,
} from '../../../lib/actions/newsletter';
import { cn } from '../../../lib/utils/classname';
import CallToAction from '../../atoms/CallToAction';
import CheckboxInput from '../../atoms/CheckboxInput';
import Heading from '../../atoms/Heading';
import FormInput from '../../molecules/FormInput';

const NewsletterFormContents: React.FC<{
  state: RegisterNewsletterContactMessageState;
}> = ({ state }) => {
  const status = useFormStatus();

  return (
    <div className="flex w-full max-w-110 flex-col items-center gap-y-4">
      <Heading as="h2" level={5} className="text-center">
        Abonnez-vous à notre newsletter
      </Heading>

      <fieldset className="flex w-full flex-col sm:flex-row">
        <FormInput
          className="grow"
          classNames={{ input: 'text-center sm:text-start' }}
          type="email"
          name="email"
          placeholder="Votre e-mail"
          autoComplete="email"
          disabled={status.pending}
          required
        />

        <CallToAction
          as="button"
          type="submit"
          variant="light"
          className="shrink-0"
          disabled={status.pending}
          loading={status.pending}
        >
          Envoyer
        </CallToAction>
      </fieldset>

      <label
        htmlFor="footer-newsletter__gdpr"
        className="flex w-full items-start"
      >
        <CheckboxInput
          name="footer-newsletter__gdpr"
          id="footer-newsletter__gdpr"
          disabled={status.pending}
          required
        />

        <span className="text-brand-primary inline-block py-1 text-sm">
          J’accepte de recevoir des e-mails et confirme avoir pris connaissance
          de la politique de confidentialité et mentions légales.
        </span>
      </label>

      <div className="flex w-full">
        <AnimatePresence>
          {state.status !== 'idle' && (
            <motion.p
              className={cn('text-xs', {
                'text-success': state.status === 'success',
                'text-danger': state.status === 'error',
              })}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
            >
              {state.status === 'success' && (
                <span>
                  Merci, votre inscription est bien confirmée, vous recevrez
                  prochainement nos actualités
                </span>
              )}

              {state.status === 'error' && (
                <span>
                  {state.message
                    ? `Une erreur est survenue lors de l’envoi du message : ${state.message}`
                    : 'Une erreur est survenue lors de l’envoi du message, veuillez réessayer plus tard'}
                </span>
              )}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const initialState: RegisterNewsletterContactMessageState = {
  status: 'idle',
};

const NewsletterForm: React.FC = () => {
  const [state, formAction, pending] = useActionState(
    registerNewsletterContact,
    initialState
  );

  return (
    <Form action={formAction} disabled={pending}>
      <NewsletterFormContents state={state} />
    </Form>
  );
};

export default NewsletterForm;
